import { Button } from "components/core/button"
import { widgetMatchesSearch } from "components/dashboard/grid/helpers"
import React, { FC, useState } from "react"
import { useInsertWidget, useSelectPublicWidgets, useSelectWidgets } from "reactQuery/widget"
import { Widget } from "domainModels/widget"
import { Layout } from "domainModels/layout"
import { FeatureName, WIDGETS_FREE_MAX_AMOUNT } from "lib/features/features"
import { useFeature } from "lib/features/useFeature"
import { Tooltip } from "components/core/tooltip"
import { useUser } from "lib/supabase/auth"
import { Link } from "components/core/link"
import { LockClosedIcon } from "@heroicons/react/24/outline"
import { ANONYMOUS_USER_ID } from "components/dashboard/anonymousConfig"
import { UiContextEntry, useUiContext } from "components/providers/uiProvider"
import { Dashboard } from "domainModels/dashboard"
import { getBuiltInWidgetConfigByWidget } from "components/dashboard/grid/helpers"
import { getModalMaxWidth, Modal } from "components/core/modal/modal"
import { CustomWidgetModal } from "components/dashboard/widgets/customWidgets/customWidgetModal"
import { Card } from "components/core/card"

interface Props {
  dashboard: Dashboard
  showExternalOnly?: boolean
  onAdded?: (widget: Widget) => void
}

export const Suggestions: FC<Props> = ({ dashboard, onAdded, showExternalOnly }) => {
  const {
    value: { searchQuery, suggestionInfo },
  } = useUiContext()
  const { data: widgets } = useSelectWidgets(dashboard?.id)
  const { data: publicWidgets } = useSelectPublicWidgets()
  const suggestions = publicWidgets?.filter((widget) => {
    const { information } = widget
    const containsSearch = searchQuery
      ? widgetMatchesSearch({
          keywords: information.keywords,
          name: information.name,
          searchQuery,
        })
      : true
    const isAlreadyOnDashboard = widgets?.some((w) => w.information.name === information.name)
    const hasIcon = !!getBuiltInWidgetConfigByWidget(widget)?.icon

    return containsSearch && !isAlreadyOnDashboard && (showExternalOnly ? !hasIcon : true)
  })

  return (
    <div className="flex gap-6">
      {suggestions?.map((suggestion) => (
        <Suggestion
          key={suggestion.id}
          dashboard={dashboard}
          widget={suggestion}
          suggestionInfo={suggestionInfo}
          onAdded={onAdded}
        />
      ))}
    </div>
  )
}

const Suggestion: FC<{
  dashboard: Dashboard
  widget: Widget
  suggestionInfo: UiContextEntry["suggestionInfo"]
  onAdded?: (widget: Widget) => void
}> = ({ dashboard, widget, suggestionInfo, onAdded }) => {
  const { user } = useUser()
  const [showModal, setShowModal] = useState(false)
  const { mutateAsync: insertWidget, isLoading: isInsertingWidget } = useInsertWidget()
  const { exhausted: widgetsExhausted } = useFeature(FeatureName.WIDGETS_AMOUNT)
  const widgetConfig = getBuiltInWidgetConfigByWidget(widget)

  const addGridWidgetToDashboard = async (widget: Widget) => {
    if (dashboard?.id) {
      const layout = suggestionInfo?.layout
      const position = suggestionInfo?.position
      const offset = position === "top" ? 1 : -1

      await insertWidget({
        dashboardId: dashboard.id,
        widget: new Widget({
          dashboard_id: dashboard.id,
          layout: new Layout({
            is_private: dashboard.is_private,
            xs: { ...widget.layout?.xs, x: layout?.xs.x ?? 0, y: layout ? layout.xs.y + offset : -1 },
            md: { ...widget.layout?.md, x: layout?.md.x ?? 0, y: layout ? layout.md.y + offset : -1 },
            lg: { ...widget.layout?.lg, x: layout?.lg.x ?? 0, y: layout ? layout.lg.y + offset : -1 },
            xl: { ...widget.layout?.xl, x: layout?.xl.x ?? 0, y: layout ? layout.xl.y + offset : -1 },
            xxl: { ...widget.layout?.xxl, x: layout?.xxl.x ?? 0, y: layout ? layout.xxl.y + offset : -1 },
            user_id: user?.id ?? ANONYMOUS_USER_ID,
          }),
          information: widget.information,
          is_private: dashboard.is_private,
          user_id: user?.id ?? ANONYMOUS_USER_ID,
        }),
      })

      onAdded?.(widget)
    }
  }

  return (
    <div>
      <Card
        showHoverEffect
        className="py-4 px-5 mt-px w-72 h-full min-h-[179px] flex flex-col justify-between rounded-md lg:rounded-xl"
        role="button"
        onClick={() => setShowModal(true)}
      >
        <div className="mb-4">
          <h3 className="inline-flex items-center text-lg leading-6 font-medium text-gray-900 dark:text-gray-200">
            {widgetConfig?.icon && (
              <span className="mr-1.5 -ml-1.5 w-5 h-5 text-indigo-800 dark:text-indigo-400">{widgetConfig.icon}</span>
            )}
            {widget.information.name}
          </h3>
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 ">{widget.information.description}</div>
        </div>
        <Tooltip
          disabled={!widgetsExhausted}
          interactive
          content={
            user ? (
              <>
                You can&apos;t add more widgets with your current plan.{" "}
                <Link to="/upgrade" className="block">
                  Upgrade now
                </Link>
              </>
            ) : (
              <>
                You can only have {WIDGETS_FREE_MAX_AMOUNT} widgets on a dashboard. To add more widgets you need to sign
                up for a pro plan.{" "}
                <Link to="/auth/registration" className="block">
                  Sign up
                </Link>
              </>
            )
          }
        >
          <Button
            disabled={widgetsExhausted}
            loading={isInsertingWidget}
            onClick={(ev) => {
              ev.stopPropagation()
              addGridWidgetToDashboard(widget)
            }}
          >
            {widgetsExhausted && <LockClosedIcon className="w-4 h-4 mr-2" />}
            Add to dashboard
          </Button>
        </Tooltip>

        <Modal show={showModal} className={getModalMaxWidth(widget)} onClose={() => setShowModal(false)}>
          {widgetConfig ? widgetConfig.renderModal({ widget }) : <CustomWidgetModal widget={widget} />}
        </Modal>
      </Card>
    </div>
  )
}
